import { Icon } from "./Icon";
import type { SavedEvalRun } from "../lib/evalPlaygroundStorage";
import type { MouseEvent } from "react";

function formatRunWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

type Props = {
  runs: SavedEvalRun[];
  activeRunId: string;
  loading?: boolean;
  onSelect: (run: SavedEvalRun) => void;
  onDelete: (runId: string, e: MouseEvent) => void;
  variant?: "inline" | "drawer";
  open?: boolean;
  onToggle?: () => void;
};

function RunList({
  runs,
  activeRunId,
  loading,
  onSelect,
  onDelete,
  fillHeight,
}: {
  runs: SavedEvalRun[];
  activeRunId: string;
  loading?: boolean;
  onSelect: (run: SavedEvalRun) => void;
  onDelete: (runId: string, e: MouseEvent) => void;
  fillHeight?: boolean;
}) {
  if (loading) {
    return <p className="text-body-sm text-on-surface-variant py-2">Loading saved runs…</p>;
  }
  if (runs.length === 0) {
    return (
      <p className="text-body-sm text-on-surface-variant py-2">
        No saved runs yet. Complete an evaluation to store results and PDR context here.
      </p>
    );
  }

  return (
    <ul
      className={`overflow-y-auto custom-scrollbar border border-outline-variant rounded bg-surface-container divide-y divide-outline-variant ${
        fillHeight ? "flex-1 min-h-0" : "max-h-52"
      }`}
    >
      {runs.map((run) => {
        const title =
          run.evidence_preview?.title || run.evidence_id.slice(0, 8) || "Evidence";
        const project =
          run.project_preview?.project_name ||
          (run.project_id ? run.project_id.slice(0, 8) : "Eval sandbox");
        const hasPdr = Boolean(
          run.pdr_context?.trim() || (run as { pdr_context_chars?: number }).pdr_context_chars,
        );
        const resultN =
          run.results?.length ||
          (run as { result_count?: number }).result_count ||
          run.models.length;
        return (
          <li
            key={run.id}
            className={`flex items-center gap-1 px-3 py-2 hover:bg-surface-container-high ${
              activeRunId === run.id ? "bg-primary/10" : ""
            }`}
          >
            <button
              type="button"
              onClick={() => onSelect(run)}
              className="flex-1 min-w-0 text-left"
            >
              <span className="text-body-sm text-on-surface block truncate">
                {title} · {project} · {resultN} model
                {resultN === 1 ? "" : "s"}
              </span>
              <span className="text-[10px] font-data-mono text-on-surface-variant flex flex-wrap gap-x-2">
                <span>{formatRunWhen(run.saved_at)}</span>
                {hasPdr ? <span className="text-tertiary">PDR saved</span> : null}
              </span>
            </button>
            <button
              type="button"
              title="Remove from history"
              onClick={(e) => onDelete(run.id, e)}
              className="p-1 text-on-surface-variant hover:text-error shrink-0"
            >
              <Icon name="close" className="text-sm" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function EvalRunHistoryPanel({
  runs,
  activeRunId,
  loading,
  onSelect,
  onDelete,
  variant = "inline",
  open = false,
  onToggle,
}: Props) {
  if (variant === "drawer") {
    return (
      <div className="flex flex-col flex-1 min-h-0 px-5 py-4">
        <RunList
          runs={runs}
          activeRunId={activeRunId}
          loading={loading}
          onSelect={onSelect}
          onDelete={onDelete}
          fillHeight
        />
      </div>
    );
  }

  return (
    <div className="border-b border-outline-variant bg-surface-container-lowest shrink-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-2.5 flex items-center justify-between gap-2 text-left hover:bg-surface-container-high/50"
      >
        <span className="font-data-label text-data-label uppercase text-on-surface-variant flex items-center gap-2">
          <Icon name="history" className="text-base text-primary" />
          Past evaluation runs
          {runs.length > 0 ? (
            <span className="text-primary normal-case">({runs.length})</span>
          ) : null}
        </span>
        <Icon
          name={open ? "expand_less" : "expand_more"}
          className="text-on-surface-variant"
        />
      </button>

      {open ? (
        <div className="px-4 pb-3">
          <RunList
            runs={runs}
            activeRunId={activeRunId}
            loading={loading}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        </div>
      ) : null}
    </div>
  );
}
